import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';

type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'tahta-theme';

export function useDarkMode() {
  // Get initial theme from localStorage or default to system
  const getInitialTheme = (): Theme => {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
    return 'system';
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [isDark, setIsDark] = useState(false);

  // Get system preference
  const getSystemPreference = useCallback(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // Update dark mode based on theme setting
  const updateDarkMode = useCallback(() => {
    let shouldBeDark = false;
    
    if (theme === 'dark') {
      shouldBeDark = true;
    } else if (theme === 'light') {
      shouldBeDark = false;
    } else {
      // System theme
      shouldBeDark = getSystemPreference();
    }

    setIsDark(shouldBeDark);

    // Update document classes
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.style.colorScheme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
    }

    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', shouldBeDark ? '#1F2937' : '#FFFFFF');
    }
  }, [theme, getSystemPreference]);

  // Change theme
  const setThemeAndSave = useCallback((newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    
    // Announce theme change
    const event = new CustomEvent('theme-changed', { detail: newTheme });
    window.dispatchEvent(event);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    if (theme === 'system') {
      // If system, switch to opposite of current state
      setThemeAndSave(isDark ? 'light' : 'dark');
    } else {
      // If manual, toggle between light and dark
      setThemeAndSave(theme === 'light' ? 'dark' : 'light');
    }
  }, [theme, isDark, setThemeAndSave]);

  // Cycle through themes
  const cycleTheme = useCallback(() => {
    const themes: Theme[] = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setThemeAndSave(themes[nextIndex]);
  }, [theme, setThemeAndSave]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (theme === 'system') {
        updateDarkMode();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, updateDarkMode]);

  // Update dark mode when theme changes
  useEffect(() => {
    updateDarkMode();
  }, [updateDarkMode]);

  // Listen for theme toggle events
  useEffect(() => {
    const handleToggle = () => toggleDarkMode();
    window.addEventListener('toggle-dark-mode', handleToggle);
    return () => window.removeEventListener('toggle-dark-mode', handleToggle);
  }, [toggleDarkMode]);

  return {
    theme,
    isDark,
    setTheme: setThemeAndSave,
    toggleDarkMode,
    cycleTheme
  };
}

// Theme context for global access

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  toggleDarkMode: () => void;
  cycleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const darkMode = useDarkMode();

  return (
    <ThemeContext.Provider value={darkMode}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}